/**
 * Demonstration data — server state.
 *
 * The reset replaces the entire program hierarchy, so on success it does not
 * try to patch caches: it clears them. Every list, detail, roster, session panel
 * and dashboard figure in the application is now about rows that no longer
 * exist, and picking the affected keys one at a time is how one gets missed and
 * a screen goes on showing a deleted program until it is reloaded.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getDemoDataStatus, resetDemoData } from '../services/api/demo-data';
import type { DemoDataResetResult, DemoDataStatus } from '../services/api/demo-data';

export const demoDataKeys = {
  status: ['demo-data', 'status'] as const,
};

/**
 * Whether this installation exposes the reset at all.
 *
 * `enabled` is the caller's business: only an ADMIN may ask, and asking as
 * anyone else answers 403, which would put an error on a screen that should
 * simply not offer the option.
 */
export function useDemoDataStatus(enabled: boolean) {
  return useQuery<DemoDataStatus>({
    queryKey: demoDataKeys.status,
    queryFn: getDemoDataStatus,
    enabled,
    // The answer comes from an environment variable; it cannot change while
    // somebody is looking at the page.
    staleTime: Infinity,
    retry: false,
  });
}

export function useResetDemoData() {
  const queryClient = useQueryClient();

  return useMutation<DemoDataResetResult, Error, void>({
    mutationFn: resetDemoData,
    onSuccess: () => {
      // Everything, deliberately - see the note at the top of this file.
      void queryClient.invalidateQueries();
    },
  });
}
